import React from "react";

import {
  View,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
  Keyboard,
} from "react-native";

import {
  getActivityModel,
  hasItems,
  hasExactMatch,
  isWeb,
  navigate,
  getElementModel,
  sortByRelevance,
  isManager,
  isAndroid,
} from "../../../helpers/utils";

import Header from "../common/header";
import Footer from "../common/footer";

import { AppStateWatcher } from "../common/AppStateWatcher";
import appStyles from "../../appStyles";
import ElementsFabButton from "./ElementsFabButton";
import ElementsList from "./list/ElementsList";
import ItemNotFound from "../common/ItemNotFound";
import MenuEditOptionsPanel from "../common/MenuEditOptionsPanel";
import Search from "../common/search-bar/Search";
import ElementsHelper from "./ElementsHelper";
import ElementItem from "./list/ElementItem";
import MergeElementConfirmDialog from "./MergeElementConfirmDialog";
import appPages from "../../../appPages";

export default class ElementsPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      isLoading: true,
      elements: [],
      showEditPanel: false,
      sets: [],
      showMerge: false,
      elementToMerge: null,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.filterResults({});
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
      filters: {},
    }));

    // Sync with server
    this.controller.getApiData("elements", {}, (elements) => {
      this.setState((state) => ({
        ...state,
        isLoading: false,
        elements: sortByRelevance(
          (elements || []).filter((a) => a.is_active == 1),
          this.state?.filters?.term,
        ),
      }));
    });
  };

  showEditOptions = (element) => {
    this.setState((state) => ({
      ...state,
      showEditPanel: true,
      selectedElement: element,
    }));
  };

  closeEditOptions = () => {
    this.setState((state) => ({
      ...state,
      selectedElement: null,
      selectedTag: null,
      showEditPanel: false,
    }));
  };

  // ------------------------------------- FILTER -------------------------------------
  filterResults = (filters) => {
    const elements = ElementsHelper.filterResults(this.controller, {
      ...filters,
      sortBy: "name",
    });

    this.setState((state) => ({
      ...state,
      isLoading: false,
      elements: sortByRelevance(elements, this.state?.filters?.term),
      filters,
    }));
  };

  filterByKeyword = ({ term }) => {
    this.filterResults({ ...this.state.filters, term });
  };

  // ------------------------------------- ACTIONS -------------------------------------
  goToArchivePage = () => {
    this.controller.changePage(appPages.archiveElementsPage, {});
  };

  addElement = (name = "") => {
    navigate(this.controller, "rn:edit_element", "changepage", {
      element: getElementModel({ name }),
    });
  };

  editElement = (selectedElement) => {
    navigate(this.controller, "rn:edit_element", "changepage", {
      element: getElementModel(selectedElement),
    });
  };

  mergeMenuElement = () => {
    const { selectedElement } = this.state;
    if (!selectedElement) return;

    this.setState((state) => ({
      ...state,
      showMerge: true,
      elementToMerge: selectedElement,
    }));
    this.closeEditOptions();
  };

  toggleMergeDialog = (targetElement) => {
    this.setState((state) => ({ ...state, targetElement }));
  };

  mergeToElement = () => {
    const { elementToMerge, targetElement } = this.state;
    if (!elementToMerge || !targetElement) return;

    this.controller.cardActionWithCallback(
      {
        action: "merge_element",
        id_from: elementToMerge.id,
        id_to: targetElement.id,
      },
      () => {
        //this.controller.cardAction({ action: "refresh" });
        this.filterResults({ ...this.state.filters });
      },
    );
    this.setState((state) => ({
      ...state,
      showMerge: false,
      elementToMerge: null,
      targetElement: null,
    }));
  };

  editMenuElement = () => {
    const { selectedElement } = this.state;
    if (selectedElement) this.editElement(selectedElement);
  };

  archiveElement = () => {
    if (isWeb) {
      this.confirmedArchive();
      this.closeEditOptions();
      return;
    }

    Alert.alert(
      "Archive element",
      "Are you sure you want to remove this element?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "OK",
          onPress: this.confirmedArchive,
        },
      ],
      { cancelable: true },
    );
  };

  confirmedArchive = () => {
    const { selectedElement } = this.state;
    if (!selectedElement) return;

    this.controller.tracking.track("Edit Element");
    console.log("Edit Element");
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "element",
        edit_id: selectedElement.id,
        edit_name: selectedElement.name,
        edit_is_active: false,
      },
      () => {
        //this.controller.cardAction({ action: "refresh" });
        this.filterResults({ ...this.state.filters });
        this.closeEditOptions();
      },
    );
  };

  copyMenuElement = () => {
    const { selectedElement } = this.state;
    if (!selectedElement) return;

    let model = getActivityModel();
    model.edit_icon = selectedElement.icon;

    navigate(this.controller, "rn:edit_element", "changepage", {
      element: model,
    });
  };

  shareMenuElement = () => {
    const { selectedElement } = this.state;
    if (!selectedElement) return;

    if (selectedElement.is_owner) {
      let model = getActivityModel(selectedElement);
      this.closeEditOptions();
      navigate(this.controller, "rn:share_element", "changepage", {
        element: model,
        selection: selectedElement.shares,
      });
    }
  };

  getMenuOptions = () => {
    const menuOptions = [];
    menuOptions.push(
      ...[
        {
          text: "Edit Element",
          action: this.editMenuElement,
        },
        {
          text: "Copy Element",
          action: this.copyMenuElement,
        },
      ],
    );
    const allowShare = isManager(this.controller?.state?.teams);
    if (allowShare) {
      menuOptions.push({
        text: "Share Element",
        action: this.shareMenuElement,
      });
    }
    menuOptions.push(
      ...[
        {
          text: "Merge into another Element",
          action: this.mergeMenuElement,
        },
        {
          text: "Archive Element",
          action: this.archiveElement,
          isDelete: true,
        },
      ],
    );

    return menuOptions;
  };

  render() {
    const {
      isLoading,
      elements,
      filters,
      showMerge,
      elementToMerge,
      targetElement,
    } = this.state;
    const flt =
      filters != null && filters.term != null ? filters.term.trim() : "";

    return (
      <View style={[appStyles.container]}>
        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          behavior="height"
          keyboardVerticalOffset={isAndroid ? 20 : 0}
        >
          <Header
            title={showMerge ? "Merge Element" : "Elements"}
            subTitle={
              showMerge
                ? "Choose an Element to merge this into"
                : "The building blocks of your life"
            }
            style={{minHeight: 105}}
            controller={this.controller}
            hideAvatar
            isLoading={isLoading}
            returnPage={appPages.homePage}
          />

          <ScrollView
            contentContainerStyle={appStyles.scrollViewContent}
            style={[
              appStyles.scrollView,
              {
                backgroundColor: "#FFF",
              },
            ]}
          >
            {showMerge ? (
              <ElementItem
                key={elementToMerge.id}
                item={elementToMerge}
                controller={this.controller}
              />
            ) : null}
            {hasItems(elements) ? (
              <ElementsList
                elements={elements}
                showEditOptions={this.showEditOptions}
                controller={this.controller}
                onElementSelected={(item) =>
                  showMerge
                    ? this.toggleMergeDialog(item)
                    : this.editElement(item)
                }
              />
            ) : null}

            {flt.length > 0 && !hasExactMatch(elements, flt) ? (
              <ItemNotFound
                name={flt}
                type="element"
                create={() => this.addElement(flt)}
              />
            ) : null}
          </ScrollView>

          <MenuEditOptionsPanel
            isPanelActive={this.state.showEditPanel}
            close={this.closeEditOptions}
            options={this.getMenuOptions()}
          />

          <View style={[appStyles.footerOptions]}>
            {!isLoading ? (
              <Search
                placeholder="Search Elements"
                filterResults={this.filterByKeyword}
                searchString={filters?.term}
                controller={this.controller}
              />
            ) : null}
          </View>

          <View style={[appStyles.footerOptions]}>
            <ElementsFabButton
              addElement={this.addElement}
              archiveElements={this.goToArchivePage}
              style={{
                bottom: 42,
              }}
            />
          </View>
        </KeyboardAvoidingView>

        {this.controller.hasNavigation() ? null : (
          <View style={appStyles.footerOptions}>
            <Footer
              controller={this.controller}
              selectedPage={appPages.elementsPage}
            />
          </View>
        )}

        {/* Merge Confirm Dialog */}
        {targetElement ? (
          <MergeElementConfirmDialog
            from={elementToMerge}
            to={targetElement}
            controller={this.controller}
            mergeElement={this.mergeToElement}
            close={() => this.toggleMergeDialog(null)}
          />
        ) : null}

        <AppStateWatcher onAppBecomesActive={() => this.componentDidFocus()} />
      </View>
    );
  }
}
